import {inject, Injectable} from '@angular/core';
import {SearchParameters, SearchParametersForOverview} from '../core/definitions/search-parameters';
import {
  CheckFilterGroup,
  HierarchicFilterGroup,
  QueryMenu,
  RangeGroup,
  SearchView,
  SearchViewPath
} from '../core/definitions/search-objects';
import {AConst} from '../core/a-const.enum';
import {QueryParserService} from '../core/query-parser.service';
import {CommonsService} from '../core/commons.service';
import {CmsQueueService} from '../core/cms-queue.service';
import {QueryFieldMenuService} from './query-field-menu.service';
import {ResultViewService} from './result-view.service';
import {MetaField} from '../core/definitions/meta-field';
import {SuperObjectModel} from '../core/definitions/super-object-model';
import {CmsApiService} from '../core/cms-api.service';
import {SearchResultViewType} from '../core/definitions/search-result-view-type.enum';
import {SolrFilterService} from '../core/solr-filter.service';
import {SearchFilters} from '../core/definitions/search-filters';
import {UserCacheService} from '../core/user-cache.service';
import {SearchContainer} from '../core/definitions/search-container';
import {SearchCategory} from '../core/definitions/extended-search/search-category';
import {LoggerService} from '../core/logger.service';
import {SearchFilterGroupEnablerService} from "./search-filter-group-enabler.service";
import {SearchViewSortOrderMenuService} from "./search-view-sort-order-menu.service";
import {SearchViewFilterService} from "./search-view-filter.service";
import {FeatureFlagsService} from "../core/feature-flags.service";
import {ensureChildOrderParentQuery, isChildFieldOrder} from './child-field-order';

@Injectable({
  providedIn: 'root'
})
export class SearchParameterService {
  // Injections
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly cmsQueue: CmsQueueService = inject(CmsQueueService);
  private readonly commons: CommonsService = inject(CommonsService);
  private readonly featureFlagService: FeatureFlagsService = inject(FeatureFlagsService);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly queryMenuService: QueryFieldMenuService = inject(QueryFieldMenuService);
  private readonly queryParser: QueryParserService = inject(QueryParserService);
  private readonly resultViewService: ResultViewService = inject(ResultViewService);
  private readonly searchFilterGroupEnablerService: SearchFilterGroupEnablerService = inject(SearchFilterGroupEnablerService);
  private readonly searchViewFilterService: SearchViewFilterService = inject(SearchViewFilterService);
  private readonly solrFilter: SolrFilterService = inject(SolrFilterService);
  private readonly sortOrderMenuService: SearchViewSortOrderMenuService = inject(SearchViewSortOrderMenuService);
  private readonly userCache: UserCacheService = inject(UserCacheService);

  private readonly nonScrollableViews = [
    SearchResultViewType.SELECTOR.toString(), SearchResultViewType.CONTENT_LIST.toString()]

  /**
   * Get search parameters to be used in call towards the CMS search_json endpoint
   * @param searchContainer a container that contains various information about the search.
   * be included in the search filter so that only objects having these ids will be returned from the search
   * @param continueScroll set to true if this is a scroll based search.
   * @param skipFacets set to true if facets should not be generated
   * @param forceAllFacets set to true if all facets should be included, even if they are disabled in the GUI
   */
  async getSearchParams(searchContainer: SearchContainer, continueScroll?: boolean, skipFacets: boolean = false,
                        forceAllFacets: boolean = false): Promise<SearchParametersForOverview> {
    await this.searchViewFilterService.setSearchViewFilters();
    const pathView = searchContainer.currentPathView || new SearchViewPath();
    const res = new SearchParametersForOverview();
    res.search_view_type = searchContainer.searchResultViewName;
    let fields = searchContainer.searchResultViewName === 'list' ? await this.getSearchFieldsSetOverviewFields(
      searchContainer) : [];
    const sort = await this.getSort(searchContainer, fields);
    if (isChildFieldOrder(searchContainer.getOrder()) && !searchContainer.orderParentQuery) {
      if (!fields.length) {
        fields = await this.getSearchFieldsSetOverviewFields(searchContainer) || [];
      }
      ensureChildOrderParentQuery(searchContainer, fields);
    }
    const rows = this.getRows(searchContainer);
    res.order_parent_query = searchContainer.orderParentQuery;

    await this.setSearchFilters(res, pathView.search_view, searchContainer);
    res.write_collections_only = pathView.search_view.write_collections_only;
    res.query = await this.getSearchQuery(searchContainer);
    res.query_orig = searchContainer.queryContainer.query;
    if (!skipFacets) {
      // Facet generation not necessary while scrolling
      res.facets = this.getFacets(pathView.search_view, searchContainer, forceAllFacets);
      res.facet_range_groups = this.getFacetRangeGroups(searchContainer)
    }
    if (pathView.search_view.stat_fields) {
      res.stat_fields = pathView.search_view.stat_fields;
    }
    if (rows !== undefined) {
      if (!this.featureFlagService.getFeatureFlags().experimental.useNewMainMenu &&
        (!res.start && !continueScroll && !this.nonScrollableViews.includes(searchContainer.searchResultViewName))) {
        res.rows = rows * 2; // this will avoid two searches in a row for virtual scrolls
      } else {
        res.rows = rows;
      }
    }


    if (searchContainer.searchRow === undefined) {
      res.start = (searchContainer.searchPage - 1) * rows;
    } else {
      this.setScrollBasedParams(searchContainer, res, continueScroll);
    }

    res.sort = sort;

    this.setCommonParams(searchContainer, res);
    this.setSearchViewBoostParams(pathView.search_view, res);
    if (searchContainer.noFullSearch) {
      res.fl = ['artifact_id'];
      res.overview = false;
    }
    if (['extended', 'advanced'].includes(searchContainer.queryContainer.selectedQueryMenu?.query_type)) {
      res.search_engine = 'extended';
      res.advanced_search_params = searchContainer.extendedSearchParams;
    } else {
      res.search_engine = searchContainer.searchEngine;
    }
    res['q.op'] = searchContainer.qOp;
    return res;
  }

  // Set search parameters that must be shared with runSearch and getSearchObjectWithOverview
  setCommonParams(searchContainer: SearchContainer, params: SearchParameters) {
    params.template_group_id = searchContainer.templateGroupId;
    params.combine_horizontal = searchContainer.combineHorizontal;
    params.overview = true;
    switch (searchContainer.searchResultViewName) {
      case SearchResultViewType.LIST:
        params.overview_fields = searchContainer.currentPathView.search_view.overview_fields;
        break;
      case SearchResultViewType.THUMBNAIL:
      case SearchResultViewType.GALLERY:
        params.overview_simple = true;
        break;
      case SearchResultViewType.LIST_THUMBNAIL:
        if (searchContainer.currentPathView.search_view.always_use_overview_fields_from_search_view) {
          params.overview_fields = searchContainer.currentPathView.search_view.overview_fields;
        }
        break;
    }
  }

  /** Copy edismax boost from SearchView (P10-1147). Views without qf unchanged. */
  private setSearchViewBoostParams(searchView: SearchView, params: SearchParameters) {
    if (!searchView?.qf) {
      return;
    }
    params.qf = searchView.qf;
    params.defType = searchView.defType || 'edismax';
  }

  async getSearchFieldsSetOverviewFields(searchContainer: SearchContainer, noCache?: boolean): Promise<MetaField[]> {
    let fields: MetaField[];
    if (searchContainer.currentPathView) {
      const curSearchView = searchContainer.currentPathView.search_view;
      fields = curSearchView.overview_fields;
      if (!searchContainer.listFieldTemplateId || searchContainer.searchResultViewName !== SearchResultViewType.LIST) {
        const meta = this.searchFiltersToMeta(curSearchView.filters);
        const objectType = meta?.object_type;
        if (!fields?.length || (objectType &&
          (searchContainer.oldTempGroupId !== undefined &&
            searchContainer.templateGroupId !== searchContainer.oldTempGroupId))) {
          if (!objectType) {
            this.logger.warn('No object type found in filters for search view');
            return [];
          }
          fields = await this.getOverviewFields(objectType, searchContainer.templateGroupId, noCache);
          searchContainer.oldTempGroupId = searchContainer.templateGroupId;
          this.setBackupSearchViewOverviewFields(searchContainer, fields);
        } else {
          searchContainer.oldTempGroupId = searchContainer.templateGroupId;
        }
      } else {
        noCache = noCache || searchContainer.refreshTemplateOverviewFields;
        fields = await this.getListFieldTemplateOverviewFields(searchContainer.listFieldTemplateId, noCache);
        searchContainer.refreshTemplateOverviewFields = false;
        this.setBackupSearchViewOverviewFields(searchContainer, fields);
      }
    }
    return fields || [];
  }

  checkFieldIsSortable(overviewField: MetaField) {
    if (this.isRelatedOverviewField(overviewField)) {
      return false;
    }
    return !(overviewField.is_array || overviewField.field_type === 'array');
  }

  private isRelatedOverviewField(overviewField: MetaField): boolean {
    return Boolean(overviewField.source_superobject_type_id)
      || Boolean(overviewField.field_name?.startsWith('$$related.'));
  }

  private setBackupSearchViewOverviewFields(searchContainer: SearchContainer, fields: MetaField[]) {
    const curSearchView = searchContainer.currentPathView.search_view;
    if (!curSearchView.$$origOverviewFields) {
      if (curSearchView.overview_fields) {
        curSearchView.$$origOverviewFields = curSearchView.overview_fields;
      } else {
        curSearchView.$$origOverviewFields = [];
      }
    }
    curSearchView.overview_fields = fields;
  }

  private searchFiltersToMeta(filters: any): SuperObjectModel {
    const filterMetaAttr = [AConst.OBJECT_TYPE, AConst.META_TYPE, 'parent_meta_type'];
    let meta: SuperObjectModel = null;
    for (const attr of filterMetaAttr) {
      let attrVal = filters[attr];
      if (attrVal) {
        if (Array.isArray(attrVal)) {
          attrVal = attrVal[0];
        }
        meta = meta || new SuperObjectModel();
        meta[attr] = attrVal;
      }
    }

    return meta;
  }

  private async getOverviewFields(objectType: string, templateGroupId: string, noCache?: boolean): Promise<MetaField[]> {
    return new Promise((resolve, reject) => {
      this.cmsQueue.runCmsFnWithQueue(
        this.cms.getModelOverviewFields,
        {
          modelName: objectType,
          type: 'overview',
          template_group_id: templateGroupId
        },
        noCache,
        (dta: any) => resolve(dta),
        (e: Error) => reject(e)
      );
    });
  }

  private async getListFieldTemplateOverviewFields(listFieldTemplateId: string, noCache?: boolean): Promise<MetaField[]> {
    return new Promise((resolve, reject) => {
      this.cmsQueue.runCmsFnWithQueue(
        this.cms.getTemplateOverviewFields,
        {template_group_id: listFieldTemplateId},
        noCache,
        (dta: any) => resolve(dta),
        (e: Error) => reject(e)
      );
    });
  }

  private async getSort(searchContainer: SearchContainer, fields: MetaField[]): Promise<string> {
    const existsAndSortable = this.checkFieldExistsAndSortable(searchContainer.getOrder(), fields);
    let res: string;
    const fieldIsInSortOrderMenus = await this.checkFieldIsInSortOrderMenus(
      searchContainer, searchContainer.getOrder());
    if (!(existsAndSortable || fieldIsInSortOrderMenus)) {
      let orderIsDefault = false;
      if (searchContainer.currentPathView?.search_view.sort_order_menus) {
        const sortOrderMenus = await this.sortOrderMenuService.getSortOrderMenus(
          searchContainer.currentPathView.search_view.sort_order_menus);
        if (sortOrderMenus) {
          res = sortOrderMenus[0].order;
          orderIsDefault = true;
        } else {
          this.logger.warn(`No sort order menus for ${searchContainer.currentPathView.search_view.title} found, using default order`);
        }
      }
      if (!res) {
        res = 'updated_at desc';
      }
      searchContainer.setOrder(res, orderIsDefault);
    }
    return this.getDynamicOrder(searchContainer)
  }

  private getDynamicOrder(searchContainer: SearchContainer): string {
    let res = searchContainer.getOrder();
    if (res.includes('{random}')) {
      res = res.replace('{random}', searchContainer.orderRandomSeed);
    }
    return res;
  }

  private getRows(searchContainer: SearchContainer) {
    let rows: number;
    if (typeof searchContainer.rows === 'number') {
      rows = searchContainer.rows;
    } else {
      rows = searchContainer.rows[searchContainer.searchResultViewName];
      if (rows) {
        if (searchContainer.displayAll) {
          rows *= searchContainer.maxPage;
        }
      } else if (searchContainer.searchResultViewName) {
        rows = this.resultViewService.calcSearchRowsAndColumns(searchContainer.searchResultViewName).rows;
      } else {
        rows = searchContainer.rows[searchContainer.searchResultViews.default];
      }
    }
    return rows;
  }

  private async setSearchFilters(searchParameters: SearchParameters,
                                 searchView: SearchView,
                                 searchContainer: SearchContainer) {
    if (searchView.filters) {
      const filters = await this.getProcessedSearchFilters(searchContainer, searchView.filters);
      this.solrFilter.setFqFromObject(searchParameters, filters);
    }
    this.setRestrictionFilters(searchParameters, searchContainer);
    this.setCheckedFilters(searchParameters, searchContainer);
    await this.setQueryFieldFilter(searchParameters, searchContainer);
    if (searchContainer.selections.includeSelectedInFilters && searchContainer.selections.selectedItems.length > 0) {
      const selectFilter = searchContainer.selections.allSelected ? '-artifact_id' : 'artifact_id';
      this.solrFilter.addFq(
        searchParameters,
        selectFilter,
        searchContainer.selections.selectedItems.map(item => item.artifact_id));
    }
    this.setHierarchicGroupFilter(searchParameters, searchContainer);
  }

  private async getSearchQuery(searchContainer: SearchContainer): Promise<string> {
    let query = '*';
    if (searchContainer.searchCategory) {
      query = SearchCategory.parseQuery(searchContainer.queryContainer.query, searchContainer.searchCategory);
    } else {
      const menu = await this.queryMenuService.getCurrentQueryMenu(searchContainer);
      if (!menu ||  ['general', 'extended', 'advanced'].includes(menu.query_type)) {
        const view = searchContainer.currentPathView?.search_view;
        const rawQuery = searchContainer.queryContainer.query;
        if ((!menu || menu.query_type === 'general') && view?.relevance_name_fields?.length) {
          query = this.queryParser.buildViewRelevanceQuery(
            view.relevance_name_fields,
            rawQuery,
            view.relevance_text_field
          ) || this.queryParser.parse(rawQuery, true);
        } else {
          query = this.queryParser.parse(rawQuery, true);
        }
      }
    }
    let searchViewQuery = searchContainer.currentPathView?.search_view.fq;
    if (searchViewQuery) {
      searchViewQuery = await this.setTargetObjectFilter(searchContainer, searchViewQuery);
      if (['*', '*:*'].includes(query)) {
        query = searchViewQuery;
      } else {
        query = `(${query}) AND ${searchViewQuery}`;
      }
    }
    return query;
  }

  private async setTargetObjectFilter(searchContainer: SearchContainer, searchViewQuery: string) {
    while (searchViewQuery.includes('{{target_object_id}}')) {
      if (!searchContainer.targetObject) {
        // This happens because the target object is not loaded yet due to search pagination component running
        // a search before target object is set
        await this.searchViewFilterService.loadSetTargetObject(searchContainer);
      }
      if (searchContainer.targetObject) {
        searchViewQuery = searchViewQuery.replace('{{target_object_id}}', searchContainer.targetObject.artifact_id);
      } else {
        this.logger.warn('Unable to set target object id in query, no target object available');
        break;
      }
    }
    return searchViewQuery;
  }

  private async getProcessedSearchFilters(searchContainer: SearchContainer,
                                          filters: SearchFilters): Promise<SearchFilters> {
    const res = new SearchFilters(filters);
    for (const key in res) {
      if (res.hasOwnProperty(key)) {
        const value = res[key];
        if (value === 'user.artifact_id') {
          const userData = await this.userCache.getUserData();
          res[key] = userData.artifact_id;
        } else if (value === '{{target_object_id}}') {
          res[key] = searchContainer.targetObject.artifact_id;
        } else if (key === '-parent_id' && value === '[* TO *]' &&
          searchContainer.queryContainer.query) {
          // Remove 'parent only' search for hierarchic searches if there is a non-empty query in order to search for
          // sub items
          res[key] = undefined;
        }
      }
    }
    return res;
  }

  private setCheckedFilters(searchParameters: SearchParameters, searchContainer: SearchContainer) {
    const checkedFilters = this.getFilterRangeValues(searchContainer, searchContainer.filtersFacets.checkedFilters);
    for (const key in checkedFilters) {
      if (checkedFilters.hasOwnProperty(key)) {
        const value = checkedFilters[key];
        const name = this.getFilterName(key, searchContainer.filtersFacets.filterGroups);
        if (value.length > 0) {
          this.solrFilter.addFq(searchParameters, name, value);
        }
      }
    }

  }

  private async setQueryFieldFilter(searchParameters: SearchParameters, searchContainer: SearchContainer) {
    let query: string;
    const qfm: QueryMenu = await this.queryMenuService.getCurrentQueryMenu(searchContainer);
    if (qfm && !['general', 'advanced', 'extended'].includes(qfm.query_type)) {
      if (searchContainer.queryContainer.query) {
        // Untrimmed padding lands inside the quoted term, and a numeric field
        // such as folder_reference_id rejects "9644 " with an HTTP 400.
        query = searchContainer.queryContainer.query.trim();
        if (qfm.query_type === 'field_upper') {
          if (query.length > 1 && query.startsWith('"') && query.endsWith('"')) {
            query = this.queryParser.toCompactUpper(query.slice(1, -1)) || '';
          } else {
            const pattern = this.queryParser.compactUpperContainsPattern(query);
            query = pattern ? `/${pattern}/` : '';
          }
        } else {
          query = query.replaceAll(' ', String.raw`\ `);
        }
        // P10-1591: prefer live menu query_field — stored-search restore may leave queryField unset.
        const fieldName = qfm.query_field || searchContainer.queryContainer.queryField;
        if (!fieldName || !query) {
          return;
        }
        this.solrFilter.addFq(searchParameters, fieldName, query);
      }
    }
  }

  private getFilterRangeValues(searchContainer: SearchContainer, filters: any) {
    // Filter values containing "range" values, e.g. "[
    const res = {};
    for (const key in filters) {
      if (filters.hasOwnProperty(key)) {
        const value = filters[key];
        res[key] = value;
        if (Array.isArray(value)) {
          const v = value[0];
          if (typeof v === 'string' && v.startsWith('[')) {
            res[key] = value[0];
          }
        } else if (typeof value === 'string' && value.startsWith('{{')) {
          res[key] = this.getMappedFilterValue(searchContainer, value);
        }
      }
    }
    return res;
  }

  private setHierarchicGroupFilter(searchParameters: SearchParameters, searchContainer: SearchContainer) {
    const hierarchicFilterGroup = this.searchViewFilterService.getHierarchicFilterGroupFromSearchView(
      searchContainer.currentPathView.search_view)
    if (hierarchicFilterGroup) {
      const ids = this.getHierarchicSearchIds(searchContainer);
      if (ids.length === 0) {
        return;
      }
      this.checkAddContextFilter(searchParameters, hierarchicFilterGroup, ids);
      this.solrFilter.addFq(
        searchParameters, this.getHierarchicFilterName(hierarchicFilterGroup), ids);
    }
  }

  private checkAddContextFilter(searchParameters: SearchParameters, hierarchicFilterGroup: HierarchicFilterGroup, ids: string[]) {
    const hierarchicContextFilter = this.getHierarchicContextFilter(hierarchicFilterGroup);
    if (hierarchicContextFilter) {
      // Get last id without the '\/'-part and the * at the end
      let contextId = ids[0].split(String.raw`\/`).slice(-1)[0].replaceAll('*', '');
      if (contextId) {
        this.solrFilter.addFq(searchParameters, `-${hierarchicContextFilter}`, contextId);
        return true;
      }
    }
  }

  private getHierarchicFilterName(hierarchicFilterGroup: HierarchicFilterGroup) {
    return (hierarchicFilterGroup.join_prefix || '') + hierarchicFilterGroup.list_filter_field;
  }

  private getHierarchicContextFilter(hierarchicFilterGroup: HierarchicFilterGroup) {
    return (hierarchicFilterGroup.join_prefix || '') + (hierarchicFilterGroup.context_filter_field || '');
  }

  private getHierarchicSearchIds(searchContainer: SearchContainer) {
    let ids = []
    let id = searchContainer.filtersFacets.hierarchicFilterGroup?.lastSelectedNode?.facet?.id;
    if (id) {
      // Special handling for place search, must escape /-character and add wildcard in order for sub-nodes to be found
      if (id.startsWith('/S')) {
        id = id.replaceAll('/S', String.raw`\/S`) + '*';
      }
      ids = [id];
    }
    return ids;
  }

  private getMappedFilterValue(searchContainer: SearchContainer, value: string) {
    if (value.includes('target_object_id')) {
      if (searchContainer.targetObject) {
        return searchContainer.targetObject.artifact_id;
      } else {
        this.logger.error('Search container target object is missing!');
      }
    }
    return value;
  }

  private getFilterName(key: string, filterGroups: CheckFilterGroup[]) {
    let joinPrefix = '';
    if (filterGroups) {
      for (const group of filterGroups) {
        const filter = group.filters.find(f => f.name === key);
        if (filter) {
          joinPrefix = filter.join_prefix || '';
          break;
        }
      }
    }
    return joinPrefix + key.split(':')[0];
  }

  private setRestrictionFilters(searchParameters: SearchParameters, searchContainer: SearchContainer) {
    const res = [];
    if (searchContainer.restrictions) {
      searchContainer.restrictions.filter(restrict => restrict.on)
        .forEach(restrict => this.solrFilter.addFq(searchParameters, restrict.fieldId, restrict.value));
    }
    return res;
  }

  private getFacets(searchView: SearchView, searchContainer: SearchContainer, forceAllFacets = false) {
    let res = searchView.facets ? this.commons.copy(searchView.facets) : [];
    let filterGroups: CheckFilterGroup[];
    if (searchContainer.filtersFacets.filterGroups?.length) {
      filterGroups = searchContainer.filtersFacets.filterGroups;
    } else {
      filterGroups = this.searchViewFilterService.getCheckFilterGroupsFromSearchView(searchView);
    }
    if (filterGroups) {
      this.searchFilterGroupEnablerService.setPreDefinedEnabledForFilterGroups(filterGroups);
      res = res.concat(filterGroups
        .filter(fGroup => fGroup.enabled || forceAllFacets)
        .flatMap(fGroup => fGroup.filters)
        .map(filter => (filter.join_prefix || '') + filter.name)
        .filter(name => !res.includes(name))
      );
    }
    const hierarchicFilterGroup = this.searchViewFilterService.getHierarchicFilterGroupFromSearchView(
      searchView);
    if (hierarchicFilterGroup) {
      res.push(this.getHierarchicFilterName(hierarchicFilterGroup));
    }
    return res;
  }

  // Facet range groups submitted to search must be enabled/disabled according to what's been enabled/disabled in GUI
  private getFacetRangeGroups(searchContainer: SearchContainer): RangeGroup[] {
    const submitGroups = this.searchViewFilterService.getFacetRangeGroupsFromSearchView(
      searchContainer.currentPathView.search_view);
    if (submitGroups && searchContainer.filtersFacets.facetRangeGroups) {
      for (const guiGroup of searchContainer.filtersFacets.facetRangeGroups) {
        const submitGroup = submitGroups.find(group => group.title === guiGroup.title);
        if (submitGroup) {
          submitGroup.enabled = guiGroup.enabled;
        }
      }
    }
    return submitGroups;
  }

  private setScrollBasedParams(searchContainer: SearchContainer, searchParameters: SearchParameters, continueScroll?: boolean) {
    const defaultRows = this.resultViewService.getDefaultCalculatedRowsColumns(searchContainer.searchResultViewName);
    const tileData = this.resultViewService.getViewTileData()[searchContainer.searchResultViewName];
    const addRows = tileData.scrollAddRows;
    const start = searchContainer.searchRow * defaultRows.columns;
    if (continueScroll) {
      searchParameters.start = start;
      searchParameters.rows = defaultRows.columns * addRows;
    } else {
      searchParameters.start = 0;
      searchParameters.rows = start + defaultRows.columns * addRows;
    }
  }

  // Check whether named fields are part of overview fields and can be used for sorting
  private checkFieldExistsAndSortable(fieldName: string, fields: MetaField[]) {
    if (!fieldName) {
      return false;
    }
    if (fieldName.startsWith(AConst.ARTIFACT_NAME) || isChildFieldOrder(fieldName)) {
      return true;
    }
    return (fields || []).some(field =>
      fieldName.includes(field.name) && this.checkFieldIsSortable(field)
    );
  }

  private async checkFieldIsInSortOrderMenus(searchContainer: SearchContainer, orderField: string) {
    if (!searchContainer?.currentPathView) {
      return false;
    }
    let sortOrderMenus = [];
    const sortOrderMenuId = searchContainer.currentPathView?.search_view?.sort_order_menus;
    if (sortOrderMenuId) {
      sortOrderMenus = await this.sortOrderMenuService.getSortOrderMenus(sortOrderMenuId);
    }

    return (sortOrderMenus || []).some(sortOrderMenu => orderField === sortOrderMenu.order);
  }

}
